
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Check, ArrowRight } from "lucide-react";
import solutions from "@/data/solutionsData";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import InquiryForm from "@/components/services/InquiryForm";

// Sample benefits and features for each solution type
const solutionDetails = {
  "business-solution": {
    benefits: [
      "Streamlined operations and processes",
      "Improved decision-making with real-time data",
      "Reduced operational costs",
      "Enhanced business efficiency",
      "Better resource allocation"
    ],
    features: [
      "End-to-end business process automation",
      "Custom dashboards and reporting",
      "Integration with existing systems",
      "Mobile access to business data",
      "Role-based access control"
    ]
  },
  "erp-solution": {
    benefits: [
      "Centralized data management",
      "Improved collaboration across departments",
      "Real-time visibility into operations",
      "Automated workflows and processes",
      "Reduced manual errors"
    ],
    features: [
      "Financial management and accounting",
      "Human resources and payroll",
      "Inventory and supply chain management",
      "Customer relationship management",
      "Business intelligence and reporting"
    ]
  },
  "ai-based-solution": {
    benefits: [
      "Automated decision-making processes",
      "Predictive analytics for business insights",
      "Reduced human error through automation",
      "Faster data processing and analysis",
      "Personalized customer experiences"
    ],
    features: [
      "Machine learning algorithms for data analysis",
      "Natural language processing capabilities",
      "Computer vision for image recognition",
      "Predictive modeling and forecasting",
      "AI-driven automation of routine tasks"
    ]
  },
  "saas-solution": {
    benefits: [
      "Reduced IT infrastructure costs",
      "Scalable solutions that grow with your business",
      "Automatic updates and maintenance",
      "Anywhere, anytime access to applications",
      "Pay-as-you-go pricing model"
    ],
    features: [
      "Cloud-based application delivery",
      "Multi-tenant architecture",
      "RESTful API integrations",
      "Mobile compatibility",
      "Data encryption and security"
    ]
  },
  "it-infrastructure": {
    benefits: [
      "Improved system reliability and uptime",
      "Enhanced security for your data",
      "Scalable infrastructure that grows with your needs",
      "Reduced IT maintenance costs",
      "Better disaster recovery capabilities"
    ],
    features: [
      "Network design and implementation",
      "Server configuration and management",
      "Cloud infrastructure setup",
      "Cybersecurity and threat protection",
      "24/7 monitoring and support"
    ]
  },
  "contact-center": {
    benefits: [
      "Improved customer satisfaction and engagement",
      "Increased agent productivity and efficiency",
      "Real-time monitoring and management",
      "Enhanced call quality and customer experience",
      "Comprehensive analytics and reporting"
    ],
    features: [
      "Multi-channel communication support",
      "Automatic call distribution",
      "Interactive voice response (IVR)",
      "Call recording and quality monitoring",
      "Advanced analytics and reporting"
    ]
  },
  "hrm-solution": {
    benefits: [
      "Streamlined HR processes and operations",
      "Improved employee engagement and satisfaction",
      "Better talent management and retention",
      "Reduced administrative overhead",
      "Enhanced compliance with labor regulations"
    ],
    features: [
      "Employee data management",
      "Recruitment and onboarding",
      "Performance management",
      "Time and attendance tracking",
      "Payroll processing and management"
    ]
  },
  "crm-solution": {
    benefits: [
      "Improved customer retention and loyalty",
      "Increased sales and revenue",
      "Better customer service and support",
      "Enhanced marketing effectiveness",
      "Data-driven decision making"
    ],
    features: [
      "Contact and lead management",
      "Sales pipeline and opportunity tracking",
      "Customer service and support",
      "Marketing campaign management",
      "Analytics and reporting"
    ]
  },
  "accounting-finance": {
    benefits: [
      "Accurate financial reporting and analysis",
      "Streamlined accounting processes",
      "Better cash flow management",
      "Enhanced budgeting and forecasting",
      "Improved financial compliance"
    ],
    features: [
      "General ledger and financial reporting",
      "Accounts payable and receivable",
      "Tax management and compliance",
      "Budget planning and control",
      "Financial analytics and dashboards"
    ]
  },
  "ecommerce-solution": {
    benefits: [
      "Expanded market reach and customer base",
      "24/7 sales capability",
      "Reduced overhead costs compared to physical stores",
      "Enhanced customer shopping experience",
      "Better inventory management"
    ],
    features: [
      "User-friendly online storefront",
      "Secure payment processing",
      "Inventory and order management",
      "Customer account management",
      "Mobile-optimized shopping experience"
    ]
  },
  "business-intelligence": {
    benefits: [
      "Data-driven decision making",
      "Identification of business opportunities",
      "Improved operational efficiency",
      "Better understanding of customer behavior",
      "Competitive advantage through analytics"
    ],
    features: [
      "Interactive dashboards and visualizations",
      "Real-time data analysis",
      "Predictive analytics and forecasting",
      "Custom reporting and KPIs",
      "Data integration from multiple sources"
    ]
  },
  "enterprise-solutions": {
    benefits: [
      "Integrated business processes across the organization",
      "Improved decision-making with enterprise-wide data",
      "Standardized operations and procedures",
      "Scalable solutions for growing enterprises",
      "Enhanced compliance and risk management"
    ],
    features: [
      "Enterprise resource planning (ERP)",
      "Supply chain management",
      "Enterprise content management",
      "Business process management",
      "Enterprise-grade security and compliance"
    ]
  }
};

const SolutionDetail = () => {
  const { id } = useParams();
  
  // Find the solution that matches the slug
  const solution = solutions.find(s => s.slug === id);

  if (!solution) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <h1 className="text-2xl font-bold text-gray-700 mb-4">Solution not found</h1>
        <Link to="/solutions">
          <Button variant="outline" className="curved-lg">Back to Solutions</Button>
        </Link>
      </div>
    );
  }

  // Get details for this solution
  const details = solutionDetails[solution.slug as keyof typeof solutionDetails] || {
    benefits: ["Customized to your needs", "Improved efficiency", "Cost-effective implementation"],
    features: ["Tailored functionality", "Integration with existing systems", "Ongoing support"]
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white dark:from-gray-900 dark:to-gray-800">
      <div className="container mx-auto px-4 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-gray-800 dark:text-white mb-4">
            {solution.title}
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            {solution.description}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          <Card className="p-6 curved-xl border-2 border-indigo-100 dark:border-indigo-800 shadow-soft">
            <h2 className="text-2xl font-bold text-indigo-700 dark:text-indigo-300 mb-6">
              Key Benefits
            </h2>
            <ul className="space-y-4">
              {details.benefits.map((benefit: string, index: number) => (
                <li key={index} className="flex items-center gap-3">
                  <div className="bg-green-100 dark:bg-green-900 p-1 rounded-full">
                    <Check className="h-5 w-5 text-green-600 dark:text-green-400" />
                  </div>
                  <span className="text-gray-700 dark:text-gray-300">{benefit}</span>
                </li>
              ))}
            </ul>
          </Card>

          <Card className="p-6 curved-xl border-2 border-indigo-100 dark:border-indigo-800 shadow-soft">
            <h2 className="text-2xl font-bold text-indigo-700 dark:text-indigo-300 mb-6">
              Features
            </h2>
            <ul className="space-y-4">
              {details.features.map((feature: string, index: number) => (
                <li key={index} className="flex items-center gap-3">
                  <div className="bg-indigo-100 dark:bg-indigo-900 p-1 rounded-full">
                    <Check className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                </li>
              ))}
            </ul>
          </Card>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md mb-16">
          <h2 className="text-2xl font-bold text-indigo-700 dark:text-indigo-300 mb-4">
            Technologies
          </h2>
          <div className="flex flex-wrap gap-3">
            {solution.technologies.map((tech) => (
              <span
                key={tech}
                className="bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200 px-4 py-2 rounded-full"
              >
                {tech}
              </span>
            ))}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="text-center"
        >
          <Sheet>
            <SheetTrigger asChild>
              <Button
                size="lg"
                className="curved-xl btn-gradient text-white shadow-md hover:shadow-lg transition-all duration-300"
              >
                Get Started
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[90vw] sm:w-[540px] overflow-y-auto curved-xl">
              <SheetHeader>
                <SheetTitle>Inquire about {solution.title}</SheetTitle>
                <SheetDescription>
                  Please fill out the form below and we'll get back to you shortly.
                </SheetDescription>
              </SheetHeader>
              <InquiryForm service={solution.title} formType="solution" />
            </SheetContent>
          </Sheet>
        </motion.div>
      </div>
    </div>
  );
};

export default SolutionDetail;
